import { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { faFileArrowUp, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { TextColors, TypographyStyles } from '@/styles'
import { Alert, Button, Dialog, Form, Icon, StepDialog, Text } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  createManualAccount,
  CREATE_MANUAL_ACCOUNT_KEY,
} from '@/actions/financialAccountActions'
import { getFetchError } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'

interface AddManualAccountModalProps {
  onClose: () => void
}

interface StepProps {
  onClose: () => void
}

const ACCOUNT_TYPE_OPTIONS = [
  { text: 'Checking', value: 'checking' },
  { text: 'Savings', value: 'savings' },
  { text: 'Credit card', value: 'credit card' },
  { text: 'Line of credit loan', value: 'line of credit' },
  { text: 'Auto loan', value: 'auto' },
]

const ACCOUNT_TYPE_MAP: Record<string, { type: string; subtype: string }> = {
  checking: { type: 'depository', subtype: 'checking' },
  savings: { type: 'depository', subtype: 'savings' },
  'credit card': { type: 'credit', subtype: 'credit card' },
  'line of credit': { type: 'loan', subtype: 'line of credit' },
  auto: { type: 'loan', subtype: 'auto' },
}

const validationSchema = yup.object({
  plaidInstitutionName: yup
    .string()
    .trim()
    .required('Institution name is required.'),
  name: yup.string().trim().required('Account name is required.'),
  mask: yup
    .string()
    .matches(/^\d*$/, 'Numbers only.')
    .length(4, 'Must be exactly 4 digits.')
    .required('Last 4 of account is required.'),
  accountType: yup.string().required('Account type is required.'),
})

const ManualAccountDisclaimer = ({
  onClose,
  onContinue,
}: StepProps & { onContinue: () => void }) => (
  <>
    <Alert
      variant="warning"
      dismissible
      title="Add an account manually?"
      className="mt-4"
    >
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.YELLOW}>
        Most banks connect automatically through Plaid. Continue only if your
        bank isn&apos;t listed.
      </Text>
    </Alert>
    <Text variant={TypographyStyles.BODY_SM} className="mt-4">
      Plaid is the technology that we use to automatically sync your
      transactions every month, but not all banks are supported. If you
      continue, you&apos;ll need to:
    </Text>
    <ul className="mt-4 flex flex-col gap-3 ml-20">
      <li className="flex items-center gap-3">
        <Icon
          icon={faPlus}
          backgroundColor="priority-bold"
          backgroundStyle="rounded"
          size="sm"
          color="text-neutral-0"
          backgroundClassName="shrink-0"
        />
        <Text variant={TypographyStyles.BODY_SM}>
          Manually enter in your financial institution&apos;s details.
        </Text>
      </li>
      <li className="flex items-center gap-3">
        <Icon
          icon={faFileArrowUp}
          backgroundColor="priority-bold"
          backgroundStyle="rounded"
          size="sm"
          color="text-neutral-0"
          backgroundClassName="shrink-0"
        />
        <Text variant={TypographyStyles.BODY_SM}>
          Upload statements manually{' '}
          <span className="font-bold">each month</span> for us to keep your
          books accurate.
        </Text>
      </li>
    </ul>
    <Dialog.Footer className="mt-6 sm:space-x-0">
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button onClick={onContinue}>Continue</Button>
    </Dialog.Footer>
  </>
)

const CreateManualAccountForm = ({ onClose }: StepProps) => {
  const dispatch = useAppDispatch()
  const error = useReselector(getFetchError, CREATE_MANUAL_ACCOUNT_KEY)

  const formik = useFormik({
    initialValues: {
      plaidInstitutionName: '',
      name: '',
      mask: '',
      accountType: '',
    },
    validateOnChange: true,
    validateOnBlur: false,
    validationSchema,
    onSubmit: async ({ accountType, ...rest }) => {
      const { type, subtype } = ACCOUNT_TYPE_MAP[accountType]
      const result = await createManualAccount({ ...rest, type, subtype })(
        dispatch
      )
      if (result) {
        onClose()
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="-mt-3"
      >
        Enter the details of your account.
      </Text>
      {error && (
        <Alert variant="error" className="mt-4">
          {error.message as string}
        </Alert>
      )}
      <div className="flex flex-col gap-4">
        <Form.Input
          name="plaidInstitutionName"
          label="Bank or Financial Institution"
        />
        <Form.Input name="name" label="Account Name" />
        <div className="flex gap-4">
          <div className="w-1/2">
            <Form.Input
              name="mask"
              label="Last 4 of Account"
              maxLength={4}
              inputMode="numeric"
              className="w-full"
            />
          </div>
          <div className="w-1/2">
            <Form.Select
              name="accountType"
              label="Account Type"
              options={ACCOUNT_TYPE_OPTIONS}
              className="w-full"
            />
          </div>
        </div>
      </div>
      <Dialog.Footer className="mt-6 sm:space-x-0">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={formik.isSubmitting} onClick={formik.submitForm}>
          Create
        </Button>
      </Dialog.Footer>
    </FormikProvider>
  )
}

export const AddManualAccountModal = ({
  onClose,
}: AddManualAccountModalProps) => {
  const [activeStep, setActiveStep] = useState(0)

  const steps = [
    {
      content: (
        <ManualAccountDisclaimer
          onClose={onClose}
          onContinue={() => setActiveStep(1)}
        />
      ),
    },
    {
      content: <CreateManualAccountForm onClose={onClose} />,
    },
  ]

  return (
    <StepDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Add an account manually"
      steps={steps}
      activeStep={activeStep}
    />
  )
}
